import React from "react";
// import "./Settlements.css"; // Assuming the new styles are from Orders.css or Bootstrap
import { Filter, Eye, X, Info } from "lucide-react"; // Import Info icon
import { FaSort, FaSortUp, FaSortDown, FaEye } from "react-icons/fa";
import { useSettlements } from "../../customHooks/Settlements/useSettlements";
import { OverlayTrigger, Tooltip } from "react-bootstrap"; // Import Tooltip components

import Pagination from "../../components/Pagination/Pagination";
import {
  formatCurrency,
  formatDateTime,
  mapStatusToBootstrap,
} from "../../components/Constants/Constants";
import { useNavigate } from "react-router-dom";
import AppLoader from "../../components/AppLoader/appLoader";
// import UnAuthorized from "../UnAuthorized/UnAuthorized";
// import usePermission from "../../utils/usePermission";
import CopyIcon from "../../components/CopyIcon/CopyIcon";

// Define column information for Settlements Tooltips
const settlementColumnInfo = {
    settlementId: "Unique identifier for the settlement.",
    orderId: "The Network Order ID associated with this settlement.",
    buyerAppId: "The ID of the Buyer Application.",
    sellerAppId: "The ID of the Seller Application.",
    status: "The current status of the settlement (e.g., COMPLETED, PENDING).",
    referenceNo: "External reference number for the transaction/payment.",
    orderAmount: "The total order value.",
    settlementAmount: "The amount actually settled or paid to the participant.",
    differenceAmount: "The difference between Order Amount and Settled Amount.",
    createdAt: "The date and time the settlement record was created.",
};


const Settlements = () => {
  const today = new Date().toISOString().slice(0, 10);
  const {
    settlements,
    loading,
    totalOrderCount,
    filters,
    setFilters,
    handleFilterChange,
    clearFilters,
    sortConfig,
    handleSort,
    paginatedSettlements,
    totalPages,
    currentPage,
    startItem,
    endItem,
    goToPage,
    itemsPerPage,
    itemsPerPageOptions,
    handleItemsPerPageChange,
    filterOptions, // Include dynamic filters
    selectedOption,
    setSelectedOption,
    searchValue,
    setSearchValue,
    handleDropdownChange,
    handleSearchChange,
    searchOptions,
  } = useSettlements();

  // const canViewSettlements = usePermission("SETTLEMENT_LISTING_VIEW");

  const navigate = useNavigate();
  return (
    <>
      {/* {canViewSettlements ? ( */}
      <div className="m-4">
        {/* Added margin to match Orders page layout */}
        <p className="text-muted small mb-3">
          This page provides a consolidated view of all recorded settlements.
          Use the filters below to search for specific settlements and click on
          a Settlement ID to view detailed information.
        </p>

        {/* --- Filters (Styled like Orders) --- */}
        <div className="filters-wrapper bg-white px-3 py-2 rounded shadow-sm">
          <div className="row gx-4 gy-3 align-items-end flex-wrap">
            {/* Search Section - Styled like Generic Search in Orders */}
            <div className="col-auto">
              <label className="form-label mb-1">Search By</label>
              <div className="input-group" style={{ minWidth: "400px" }}>
                <select
                  className="form-select"
                  value={selectedOption}
                  onChange={handleDropdownChange}
                  style={{ maxWidth: "150px" }}
                >
                  {searchOptions?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  className="form-control"
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder={`Enter ${selectedOption
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}`}
                  style={{
                    // Removed specific width and border-radius override, relying on input-group
                    fontSize: "12px",
                    padding: "none",
                  }}
                />
              </div>
            </div>

            {/* Dynamic Filters */}
            {/* Buyer App */}
            <div className="col-auto">
              <label className="form-label mb-1">Buyer App</label>
              <select
                className="form-select"
                value={filters.buyer_app_id || ""}
                onChange={(e) =>
                  handleFilterChange("buyer_app_id", e.target.value)
                }
                // Style applied for minWidth for alignment
                style={{ minWidth: "150px" }}
              >
                <option value="">All</option>
                {filterOptions?.buyerAppIds?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {/* Settlement Status */}
            <div className="col-auto">
              <label className="form-label mb-1">Settlement Status</label>
              <select
                className="form-select"
                value={filters.settlement_status || ""}
                onChange={(e) =>
                  handleFilterChange("settlement_status", e.target.value)
                }
                style={{ minWidth: "150px" }}
              >
                <option value="">All</option>
                {filterOptions?.settlementStatuses?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-auto">
              <label className="form-label mb-1">Created From</label>
              <input
                type="date"
                className="form-control"
                value={filters.settlement_created_at_from}
                onChange={(e) =>
                  handleFilterChange(
                    "settlement_created_at_from",
                    e.target.value
                  )
                }
                max={today}
                style={{
                  fontSize: "12px",
                  padding: "none",
                  minWidth: "130px",
                }}
                title="From Date"
              />
            </div>

            {/* TO Date */}
            <div className="col-auto">
              <label className="form-label mb-1">Created To</label>
              <input
                type="date"
                className="form-control"
                value={filters.settlement_created_at_to}
                onChange={(e) =>
                  handleFilterChange("settlement_created_at_to", e.target.value)
                }
                max={today}
                min={filters.settlement_created_at_from || ""}
                style={{
                  fontSize: "12px",
                  padding: "none",
                  minWidth: "130px",
                }}
                title="To Date"
              />
            </div>

            {/* Clear Filters Button - Styled like Orders Clear Filters button */}
            <div className="col-auto d-flex">
              <button
                type="button"
                className="btn btn-primary no-hover-btn"
                style={{ padding: "5px 8px" }}
                onClick={clearFilters}
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Orders table */}
        {loading ? (
          <AppLoader />
        ) : paginatedSettlements && paginatedSettlements.length > 0 ? (
          <>
            <div
              className="table-responsive mt-4"
              style={{ overflowX: "auto" }}
            >
              <table
                className="table table-bordered table-hover align-middle text-center"
              >
                <thead className="table-light text-center">
                  <tr>
                    {/* Settlement ID Header with Info icon */}
                    <th>
                        Settlement ID
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{settlementColumnInfo.settlementId}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>

                    {/* Order ID Header with Info icon */}
                    <th>
                        Order ID
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{settlementColumnInfo.orderId}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>

                    {/* Buyer App ID Header with Info icon */}
                    <th>
                        Buyer App ID
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{settlementColumnInfo.buyerAppId}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>

                    {/* Seller App Id Header (no info in original Orders columnInfo, keeping optional) */}
                    <th>
                        Seller App Id
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{settlementColumnInfo.sellerAppId}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>

                    {/* Status Header with Info icon */}
                    <th>
                        Status
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{settlementColumnInfo.status}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>

                    {/* Reference No. Header */}
                    <th>Reference No.</th> 
                    {/* Order Amt Header with Sort and Info icon */}
                    <th
                      onClick={() => handleSort("order_amount")}
                      role="button"
                      className="cursor-pointer"
                    >
                      Order Amt &nbsp;
                      {sortConfig.key === "order_amount" ? (
                        sortConfig.direction === "asc" ? (
                          <FaSortUp size={14} />
                        ) : (
                          <FaSortDown size={14} />
                        )
                      ) : (
                        <FaSort size={14} />
                      )}
                      <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{settlementColumnInfo.orderAmount}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>

                    {/* Settlement Amt Header with Sort and Info icon */}
                    <th
                      onClick={() => handleSort("settled_amount")}
                      role="button"
                      className="cursor-pointer"
                    >
                      Settlement Amt &nbsp;
                      {sortConfig.key === "settled_amount" ? (
                        sortConfig.direction === "asc" ? (
                          <FaSortUp size={14} />
                        ) : (
                          <FaSortDown size={14} />
                        )
                      ) : (
                        <FaSort size={14} />
                      )}
                      <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{settlementColumnInfo.settlementAmount}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>

                    {/* Difference Amt Header with Sort and Info icon */}
                    <th
                      onClick={() => handleSort("settlement_diff_amount")}
                      role="button"
                      className="cursor-pointer"
                    >
                      Difference Amt &nbsp;
                      {sortConfig.key === "settlement_diff_amount" ? (
                        sortConfig.direction === "asc" ? (
                          <FaSortUp size={14} />
                        ) : (
                          <FaSortDown size={14} />
                        )
                      ) : (
                        <FaSort size={14} />
                      )}
                      <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{settlementColumnInfo.differenceAmount}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>

                    {/* Created At Header with Sort and Info icon */}
                    <th
                      onClick={() => handleSort("createdAt")}
                      role="button"
                      className="cursor-pointer"
                    >
                      Created At &nbsp;
                      {sortConfig.key === "createdAt" ? (
                        sortConfig.direction === "asc" ? (
                          <FaSortUp size={14} />
                        ) : (
                          <FaSortDown size={14} />
                        )
                      ) : (
                        <FaSort size={14} />
                      )}
                      <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{settlementColumnInfo.createdAt}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSettlements.map((settlement, index) => (
                    <tr key={`${settlement.settlement_id}-${index}`}>
                        {/* Settlement ID */}
                        <td className="copy-cell">
                          <span
                            onClick={() =>
                              navigate(
                                `/settlements-detail/${settlement.settlement_id}`
                              )
                            }
                            style={{
                              cursor: "pointer",
                              color: "#0d6efd",
                              fontWeight: 500,
                              textDecoration: "none",
                            }}
                            title="View Settlements Details"
                            className="truncate-cell"
                          >
                            {settlement.settlement_id}
                          </span>
                          <CopyIcon textToCopy={settlement.settlement_id} />
                        </td>

                        {/* Order ID */}
                        <td className="copy-cell">
                          <span className="truncate-cell">
                            {settlement.order_id}
                          </span>
                          <CopyIcon textToCopy={settlement.order_id}/>
                        </td>

                        {/* Buyer App ID */}
                        <td className="copy-cell">
                          <span className="truncate-cell">
                            {settlement.buyer_app_id}
                          </span>
                          <CopyIcon textToCopy={settlement.buyer_app_id} />
                        </td>

                        {/* Seller App ID */}
                        <td className="copy-cell">
                          <span className="truncate-cell">
                            {settlement.seller_app_id}
                          </span>
                          <CopyIcon textToCopy={settlement.seller_app_id} />
                        </td>

                        {/* Status */}
                        <td>
                          {settlement.settlement_status}
                        </td>

                        {/* Reference No. */}
                        <td>
                          {settlement.settlement_reference_no}
                        </td>

                        {/* Order Amount */}
                        <td>
                          {formatCurrency(settlement.order_amount)}
                        </td>

                        {/* Settlement Amount */}
                        <td>
                          {formatCurrency(settlement.inter_participant_amount)}
                        </td>

                        {/* Difference Amount */}
                        <td>
                          {formatCurrency(
                            settlement.inter_participant_diff_amt
                          )}
                        </td>

                        {/* Created At */}
                        <td>
                          {formatDateTime(settlement.createdAt)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#9ca3af",
              fontSize: "14px",
              marginTop: "16px",
            }}
          >
            No Settlements Found
          </div>
        )}

        {/* Pagination */}
        {totalOrderCount > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            startItem={startItem}
            endItem={endItem}
            totalItems={totalOrderCount}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            onPageChange={goToPage}
            itemsPerPageOptions={itemsPerPageOptions}
          />
        )}
      </div>
      {/* // ) : (
      //   <UnAuthorized />
      // )} */}
    </>
  );
};

export default Settlements;